import type { LandingCopy } from "./types";

export const en: LandingCopy = {
  localeName: "English",
  nav: {
    games: "Games",
    studio: "Studio",
    privacy: "Privacy",
    support: "Support"
  },
  hero: {
    eyebrow: "Indie games studio",
    titleLineOne: "New",
    titleLineTwo: "Vision",
    titleLineThree: "Of Play",
    subtitle:
      "We give classic board games a second life\nwith sharper rules, deeper choices, and\nmodern ways to play.",
    viewGames: "View Games",
    playNow: "Dice Triangles"
  },
  featured: {
    eyebrow: "Featured game",
    title: "Dice Triangles",
    appStore: "App Store",
    googlePlay: "Google Play"
  },
  games: {
    eyebrow: "Our games",
    title: "Strategy games from NVP",
    allGames: "View lineup",
    comingSoon: "Coming soon",
    learnMore: "Learn more"
  },
  details: {
    back: "NVP Games",
    howEyebrow: "How to play",
    howTitle: "Roll, connect, capture, score",
    modesEyebrow: "Game modes",
    modesTitle: "One rule set, multiple ways to play"
  },
  how: {
    eyebrow: "How it works",
    title: "Tiny rules. Big decisions.",
    steps: [
      {
        title: "Roll",
        body: "Dice shape the move space so every turn starts with a new tactical puzzle."
      },
      {
        title: "Connect",
        body: "Draw clean lines across the board and set traps for the next capture."
      },
      {
        title: "Capture",
        body: "Close triangles, chain points, and turn the board into your territory."
      }
    ]
  },
  online: {
    eyebrow: "Online experience",
    title: "Rooms, rematches, rating",
    body:
      "The platform is built around lightweight multiplayer loops: quick rooms, fair rematches, recoverable sessions, and visible progression.",
    stats: [
      { value: "1v1", label: "Fast duels" },
      { value: "ELO", label: "Global rating" },
      { value: "CPU", label: "Offline practice" }
    ]
  },
  studio: {
    eyebrow: "Why NVP Games",
    title: "Made for players who like to think fast",
    body:
      "We care about games that feel immediate on the first tap and deeper on the hundredth match.",
    values: [
      {
        title: "Smart gameplay",
        body: "Easy to learn, hard to master."
      },
      {
        title: "Real players",
        body: "Compete globally in real time."
      },
      {
        title: "Climb and rank",
        body: "Rise through rating and prove your skill."
      },
      {
        title: "Indie at heart",
        body: "Small team focus, player-first decisions."
      }
    ]
  },
  cta: {
    title: "Ready to play?",
    body: "Join the first wave of players and help shape the next generation of NVP strategy games.",
    button: "View Dice Triangles"
  },
  footer: {
    tagline: "Modern strategy games for the next generation.",
    rights: "All rights reserved.",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
      { label: "Support", href: "/support" }
    ]
  },
  legal: {
    privacyTitle: "Privacy Policy",
    termsTitle: "Terms of Service",
    updated: "Last updated: October 2, 2026",
    privacySections: [
      {
        title: "Overview",
        body: [
          "NVP Games builds games and related websites. This policy explains what data may be processed when you use our games, websites, support tools, and store pages.",
          "NVP Software LLC publishes Dice Triangles, Witch's Broom, and Bridges (Bridges Game). This policy covers all three games and their related services.",
          "Our games are designed to avoid unnecessary personal profiles. We do not sell personal data."
        ]
      },
      {
        title: "Data we may process",
        body: [
          "Games may use anonymous player identifiers, generated public aliases, gameplay progress, rating, matchmaking state, diagnostics, crash reports, analytics events, advertising identifiers, and support messages you choose to send.",
          "Bridges uses Google Analytics for Firebase to understand app usage, including screens visited, session timing, time spent in the app, and mobile operating system information.",
          "Private nickname choices are stored on your device for local display. Other online players see generated aliases instead of your private nickname.",
          "When a game offers Google Play Games or Game Center features, platform services may provide a player ID, display name, leaderboard score, or similar game-service identifier so we can connect platform identity and leaderboard features.",
          "Store platforms, advertising networks, analytics providers, and device platforms may process data according to their own policies."
        ]
      },
      {
        title: "How we use data",
        body: [
          "We use data to operate online play, improve stability, prevent abuse, understand feature health, show ads where enabled, and respond to support requests.",
          "When applicable law requires consent for optional analytics or tracking, we request it before enabling that processing. Consent can be withdrawn without changing the lawfulness of earlier processing.",
          "Diagnostics exports are used only to investigate issues and improve the product."
        ]
      },
      {
        title: "Retention and data requests",
        body: [
          "We retain operational data only for as long as it is useful for gameplay, safety, support, analytics, or legal needs.",
          "For Bridges, data you submit is kept while you use the app and for up to 12 months afterward. Automatically collected data is kept for up to 24 months. Legal requirements may extend these periods; fully anonymized statistics may be kept longer.",
          "Our games do not provide user accounts. Most analytics, crash, advertising, and gameplay records are anonymous or pseudonymous, so we usually cannot identify a particular player or delete records tied to that player unless the request includes enough technical identifiers to locate them.",
          "For privacy questions or help with data you can identify, contact support@nvpgames.com."
        ]
      },
      {
        title: "Your privacy choices",
        body: [
          "Depending on applicable law, you can ask to access, correct, or delete personal data we hold, or withdraw consent, by contacting support@nvpgames.com. Include enough information to help us find the relevant records.",
          "California residents may also request information about data collection and exercise applicable sale or sharing opt-out rights without discrimination.",
          "Uninstalling a game stops further collection by that app on your device. It does not erase data already sent to us or service providers; you can contact us to request deletion."
        ]
      },
      {
        title: "Service providers and security",
        body: [
          "Service providers may process data in other countries. We apply safeguards for international transfers when required by applicable law.",
          "We use reasonable measures to protect information and provide notice of a personal data breach when legally required."
        ]
      },
      {
        title: "Children",
        body: [
          "Where the law requires a parent or guardian's consent, children should use the relevant features only with that consent. Contact support@nvpgames.com if you believe a child has provided personal data without required consent."
        ]
      },
      {
        title: "Contact",
        body: ["For privacy or support questions, contact support@nvpgames.com."]
      }
    ],
    termsSections: [
      {
        title: "Use of our games",
        body: [
          "Use NVP Games products fairly, lawfully, and without disrupting other players or services.",
          "These terms apply to Dice Triangles, Witch's Broom, and Bridges (Bridges Game), published by NVP Software LLC, and their related services.",
          "We may update, rebalance, suspend, or discontinue features when needed to keep the games healthy."
        ]
      },
      {
        title: "License and ownership",
        body: [
          "You receive a limited, non-exclusive, non-transferable, revocable license to install and use our games for personal or internal business purposes, subject to these terms.",
          "Rights in the games' code, artwork, designs, names, and branding remain with NVP Software LLC and the respective rights holders. Redistribution, modification, and reverse engineering require permission, except where applicable law permits them."
        ]
      },
      {
        title: "Online play",
        body: [
          "Online features may depend on network availability, third-party services, platform accounts, and backend systems.",
          "Ratings, rooms, tickets, and matchmaking systems may change as games evolve."
        ]
      },
      {
        title: "Ads and stores",
        body: [
          "Some games may show ads or link to platform stores. Store purchases, downloads, and platform services are governed by the relevant platform terms.",
          "Your mobile provider may charge for data or roaming used by online features. Any applicable mandatory consumer rights remain in effect."
        ]
      },
      {
        title: "Contact",
        body: ["Questions about these terms can be sent to support@nvpgames.com."]
      }
    ]
  },
  support: {
    title: "Support",
    body:
      "Tell us what happened, which game you were playing, and the device or store version if you know it.",
    emailLabel: "Email support"
  }
};
